import React, { useCallback, useEffect, useState } from 'react';
import { adminService } from '../../services/adminService';

interface RegimeRow {
  tag?: string;
  asOfTs?: string;
  features?: Record<string, number | string | null>;
  meta?: Record<string, unknown>;
}

interface SymbolState {
  symbol: string;
  gex: RegimeRow | null;
  cot: RegimeRow | null;
}

interface EngineConfig {
  engineId: string;
  lambdaName: string;
  regimeMode: 'disabled' | 'shadow' | 'active';
  regimeWeight: number;
  regimeFeatures: string[] | null;
  lastModified: string | null;
  modifiedBy: string | null;
}

interface KillSwitch {
  enabled: boolean;
  lastModified: string | null;
  modifiedBy: string | null;
  notes: string | null;
}

interface StateResponse {
  killSwitch: KillSwitch;
  symbols: SymbolState[];
  engines: EngineConfig[];
  fetchedAt: string;
}

interface RecentRow {
  timestamp: string;
  engine: string;
  symbol: string;
  baseScore: number;
  adjustedScore: number;
  adjustment: number;
  gexTag: string;
  cotTag: string | null;
}

const ENGINE_OPTIONS: Array<{ id: string; label: string }> = [
  { id: 'notches-options-spx', label: 'SPX options' },
  { id: 'notches-options-index', label: 'Index options (SPY/QQQ/IWM)' },
  { id: 'notches-options-futures', label: 'Futures options (ES/NQ/MES/MNQ)' },
  { id: 'notches-stocks', label: 'Stocks' },
  { id: 'plan-b1b', label: 'Plan B1b analyzer' },
];

const MODE_OPTIONS: Array<EngineConfig['regimeMode']> = ['disabled', 'shadow', 'active'];

function fmtET(iso: string | null | undefined): string {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
  } catch { return iso; }
}

function fmtNum(v: number | string | null | undefined, digits = 2): string {
  if (v === null || v === undefined) return '-';
  const n = typeof v === 'string' ? parseFloat(v) : v;
  if (!Number.isFinite(n)) return '-';
  return n.toFixed(digits);
}

// Inline color hints for mode badges -- dark-theme friendly. Background is
// translucent so the page's #0f1117 shows through; text picks up a
// high-contrast color.
const MODE_BADGE_STYLE: Record<string, React.CSSProperties> = {
  active:   { background: 'rgba(34, 197, 94, 0.18)',  color: '#86efac', borderColor: 'rgba(34, 197, 94, 0.35)' },
  shadow:   { background: 'rgba(245, 158, 11, 0.18)', color: '#fcd34d', borderColor: 'rgba(245, 158, 11, 0.35)' },
  disabled: { background: 'rgba(148, 163, 184, 0.15)', color: '#cbd5e1', borderColor: 'rgba(148, 163, 184, 0.3)' },
};

function modeBadge(mode: string): React.CSSProperties {
  return {
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: '12px',
    border: '1px solid',
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    fontWeight: 600,
    ...(MODE_BADGE_STYLE[mode] || MODE_BADGE_STYLE.disabled),
  };
}

export const AdminRegime: React.FC = () => {
  const [state, setState] = useState<StateResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingEngine, setEditingEngine] = useState<string | null>(null);
  const [editMode, setEditMode] = useState<string>('disabled');
  const [editWeight, setEditWeight] = useState<string>('0.05');
  const [savingEngine, setSavingEngine] = useState<string | null>(null);

  const [recentEngine, setRecentEngine] = useState<string>('notches-options-spx');
  const [recentDays, setRecentDays] = useState<number>(1);
  const [recentRows, setRecentRows] = useState<RecentRow[]>([]);
  const [recentLoading, setRecentLoading] = useState(false);
  const [recentError, setRecentError] = useState<string | null>(null);
  const [recentMeta, setRecentMeta] = useState<{ rowCount?: number } | null>(null);

  const fetchState = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminService.getRegimeState();
      setState(data);
    } catch (e: any) {
      setError(e?.message || 'Failed to load regime state');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchState(); }, [fetchState]);

  const fetchRecent = useCallback(async () => {
    setRecentLoading(true);
    setRecentError(null);
    try {
      const data = await adminService.getRegimeRecent(recentEngine, recentDays);
      setRecentRows(data.rows || []);
      setRecentMeta({ rowCount: data.rowCount });
    } catch (e: any) {
      setRecentError(e?.message || 'Failed to load recent activity');
      setRecentRows([]);
    } finally {
      setRecentLoading(false);
    }
  }, [recentEngine, recentDays]);

  const beginEdit = (engine: EngineConfig) => {
    setEditingEngine(engine.engineId);
    setEditMode(engine.regimeMode);
    setEditWeight(String(engine.regimeWeight));
  };

  const cancelEdit = () => setEditingEngine(null);

  const saveEdit = async (engineId: string) => {
    setSavingEngine(engineId);
    try {
      const w = parseFloat(editWeight);
      if (!Number.isFinite(w) || w < 0 || w > 1) {
        alert('Weight must be a number between 0 and 1');
        setSavingEngine(null);
        return;
      }
      await adminService.updateRegimeEngine(engineId, {
        regimeMode: editMode,
        regimeWeight: w,
      });
      setEditingEngine(null);
      await fetchState();
    } catch (e: any) {
      alert(`Update failed: ${e?.message || 'unknown error'}`);
    } finally {
      setSavingEngine(null);
    }
  };

  // Dark-theme-friendly utility colors
  const mutedTextColor = 'rgba(255, 255, 255, 0.5)';
  const subduedBackground = 'rgba(255, 255, 255, 0.04)';

  return (
    <>
      <p style={{ color: mutedTextColor, fontSize: '14px', marginTop: 0 }}>
        Per-symbol regime state (GEX + COT) and per-engine config (disabled / shadow / active).
        Mode and weight changes take effect on the next signal-generator run; no Lambda redeploy needed.
      </p>

      {/* Kill switch */}
      {state?.killSwitch && (
        <div style={{
          padding: '12px 16px',
          marginBottom: '20px',
          borderRadius: '8px',
          border: '1px solid',
          background: state.killSwitch.enabled ? 'rgba(34, 197, 94, 0.10)' : 'rgba(239, 68, 68, 0.10)',
          borderColor: state.killSwitch.enabled ? 'rgba(34, 197, 94, 0.35)' : 'rgba(239, 68, 68, 0.35)',
          color: state.killSwitch.enabled ? '#86efac' : '#fca5a5',
        }}>
          <strong>Master kill switch:</strong>{' '}
          {state.killSwitch.enabled ? 'ENABLED (service active)' : 'DISABLED (service off)'}
          <span style={{ marginLeft: '14px', fontSize: '12px', color: mutedTextColor }}>
            last modified {fmtET(state.killSwitch.lastModified)} by {state.killSwitch.modifiedBy || '-'}
          </span>
        </div>
      )}

      {loading && <p style={{ color: mutedTextColor }}>Loading regime state...</p>}
      {error && <p style={{ color: '#fca5a5' }}>Error: {error}</p>}

      <div style={{ marginBottom: '20px' }}>
        <button onClick={fetchState} disabled={loading} className="admin-btn-primary">
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
        {state?.fetchedAt && (
          <span style={{ marginLeft: '12px', fontSize: '12px', color: mutedTextColor }}>
            fetched {fmtET(state.fetchedAt)}
          </span>
        )}
      </div>

      {/* Engines */}
      {state?.engines && (
        <section style={{ marginBottom: '32px' }}>
          <h3 style={{ color: 'rgba(255, 255, 255, 0.9)', marginBottom: '12px' }}>Engine configuration</h3>
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Engine</th>
                  <th>Lambda</th>
                  <th>Mode</th>
                  <th style={{ textAlign: 'right' }}>Weight</th>
                  <th>Last modified (ET)</th>
                  <th>By</th>
                  <th style={{ textAlign: 'right' }}>Edit</th>
                </tr>
              </thead>
              <tbody>
                {state.engines.map((e) => {
                  const isEditing = editingEngine === e.engineId;
                  const isSaving = savingEngine === e.engineId;
                  return (
                    <tr key={e.engineId}>
                      <td><code style={{ color: '#93a8f4' }}>{e.engineId}</code></td>
                      <td style={{ color: mutedTextColor, fontSize: '12px' }}>{e.lambdaName}</td>
                      <td>
                        {isEditing ? (
                          <select
                            className="admin-select"
                            value={editMode}
                            onChange={(ev) => setEditMode(ev.target.value)}
                            style={{ padding: '4px 8px', minWidth: '110px' }}
                          >
                            {MODE_OPTIONS.map((m) => <option key={m} value={m}>{m}</option>)}
                          </select>
                        ) : (
                          <span style={modeBadge(e.regimeMode)}>{e.regimeMode}</span>
                        )}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {isEditing ? (
                          <input
                            type="number" min="0" max="1" step="0.01"
                            value={editWeight}
                            onChange={(ev) => setEditWeight(ev.target.value)}
                            className="admin-select"
                            style={{ width: '80px', textAlign: 'right' }}
                          />
                        ) : (
                          e.regimeWeight.toFixed(3)
                        )}
                      </td>
                      <td style={{ fontSize: '12px' }}>{fmtET(e.lastModified)}</td>
                      <td style={{ fontSize: '12px', color: mutedTextColor }}>{e.modifiedBy || '-'}</td>
                      <td style={{ textAlign: 'right' }}>
                        {isEditing ? (
                          <>
                            <button
                              onClick={() => saveEdit(e.engineId)}
                              disabled={isSaving}
                              className="admin-btn-primary"
                              style={{ marginRight: '6px', padding: '4px 10px', fontSize: '12px' }}
                            >
                              {isSaving ? '...' : 'Save'}
                            </button>
                            <button
                              onClick={cancelEdit}
                              disabled={isSaving}
                              className="admin-btn-clear"
                              style={{ padding: '4px 10px', fontSize: '12px' }}
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => beginEdit(e)}
                            className="admin-btn-filter"
                            style={{ padding: '4px 10px', fontSize: '12px' }}
                          >
                            Edit
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Symbols */}
      {state?.symbols && (
        <section style={{ marginBottom: '32px' }}>
          <h3 style={{ color: 'rgba(255, 255, 255, 0.9)', marginBottom: '12px' }}>Per-symbol regime state</h3>
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Symbol</th>
                  <th>GEX tag</th>
                  <th style={{ textAlign: 'right' }}>Spot</th>
                  <th style={{ textAlign: 'right' }}>Flip</th>
                  <th style={{ textAlign: 'right' }}>Call wall</th>
                  <th style={{ textAlign: 'right' }}>Put wall</th>
                  <th>GEX as-of (ET)</th>
                  <th>COT tag</th>
                  <th>COT as-of</th>
                </tr>
              </thead>
              <tbody>
                {state.symbols.map((s) => {
                  const gexFeat = s.gex?.features || {};
                  return (
                    <tr key={s.symbol}>
                      <td><strong style={{ color: 'rgba(255, 255, 255, 0.95)' }}>{s.symbol}</strong></td>
                      <td>
                        {s.gex?.tag
                          ? <code style={{ color: '#93a8f4', fontSize: '12px' }}>{s.gex.tag}</code>
                          : <span style={{ color: mutedTextColor, fontStyle: 'italic' }}>no data</span>}
                      </td>
                      <td style={{ textAlign: 'right' }}>{fmtNum(gexFeat.spot_at_compute as number, 2)}</td>
                      <td style={{ textAlign: 'right' }}>{fmtNum(gexFeat.gex_flip_strike as number, 2)}</td>
                      <td style={{ textAlign: 'right' }}>{fmtNum(gexFeat.call_wall_strike as number, 2)}</td>
                      <td style={{ textAlign: 'right' }}>{fmtNum(gexFeat.put_wall_strike as number, 2)}</td>
                      <td style={{ fontSize: '12px' }}>{fmtET(s.gex?.asOfTs)}</td>
                      <td>
                        {s.cot?.tag
                          ? <code style={{ color: '#93a8f4', fontSize: '12px' }}>{s.cot.tag}</code>
                          : <span style={{ color: mutedTextColor, fontStyle: 'italic' }}>n/a</span>}
                      </td>
                      <td style={{ fontSize: '12px', color: mutedTextColor }}>{s.cot?.asOfTs || '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Recent activity */}
      <section>
        <h3 style={{ color: 'rgba(255, 255, 255, 0.9)', marginBottom: '12px' }}>Recent shadow / active log lines</h3>
        <div className="admin-filters" style={{ marginBottom: '12px' }}>
          <select
            className="admin-select"
            value={recentEngine}
            onChange={(e) => setRecentEngine(e.target.value)}
          >
            {ENGINE_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
          <select
            className="admin-select"
            value={recentDays}
            onChange={(e) => setRecentDays(Number(e.target.value))}
            style={{ marginLeft: '8px', maxWidth: '120px' }}
          >
            {[1, 3, 7, 14].map((d) => <option key={d} value={d}>{d} day(s)</option>)}
          </select>
          <button
            onClick={fetchRecent}
            disabled={recentLoading}
            className="admin-btn-primary"
            style={{ marginLeft: '8px' }}
          >
            {recentLoading ? 'Loading...' : 'Load'}
          </button>
          {recentMeta && (
            <span style={{ marginLeft: '14px', fontSize: '12px', color: mutedTextColor }}>
              {recentMeta.rowCount} row(s)
            </span>
          )}
        </div>

        {recentError && <p style={{ color: '#fca5a5' }}>Error: {recentError}</p>}

        {recentRows.length > 0 ? (
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Timestamp (ET)</th>
                  <th>Symbol</th>
                  <th style={{ textAlign: 'right' }}>Base</th>
                  <th style={{ textAlign: 'right' }}>Adjusted</th>
                  <th style={{ textAlign: 'right' }}>Delta</th>
                  <th>GEX</th>
                  <th>COT</th>
                </tr>
              </thead>
              <tbody>
                {recentRows.slice(0, 100).map((r, i) => (
                  <tr key={i}>
                    <td style={{ fontSize: '12px' }}>{fmtET(r.timestamp)}</td>
                    <td><strong style={{ color: 'rgba(255, 255, 255, 0.95)' }}>{r.symbol}</strong></td>
                    <td style={{ textAlign: 'right' }}>{r.baseScore.toFixed(4)}</td>
                    <td style={{ textAlign: 'right' }}>{r.adjustedScore.toFixed(4)}</td>
                    <td style={{
                      textAlign: 'right',
                      color: r.adjustment > 0 ? '#86efac' : r.adjustment < 0 ? '#fca5a5' : mutedTextColor,
                      fontWeight: r.adjustment !== 0 ? 600 : 400,
                    }}>
                      {r.adjustment >= 0 ? '+' : ''}{r.adjustment.toFixed(4)}
                    </td>
                    <td style={{ fontSize: '12px' }}><code style={{ color: '#93a8f4' }}>{r.gexTag}</code></td>
                    <td style={{ fontSize: '12px' }}>
                      {r.cotTag ? <code style={{ color: '#93a8f4' }}>{r.cotTag}</code>
                                : <span style={{ color: mutedTextColor }}>-</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          !recentLoading && !recentError && (
            <div style={{
              padding: '16px',
              borderRadius: '8px',
              border: '1px dashed rgba(255, 255, 255, 0.1)',
              background: subduedBackground,
              color: mutedTextColor,
              fontStyle: 'italic',
              fontSize: '13px',
            }}>
              No shadow activity yet for this engine in the selected window. Shadow data only accumulates after the engine
              is flipped to <code style={{ color: '#fcd34d' }}>shadow</code> mode AND the signal-generator Lambda runs
              (usually pre-market and intraday on weekdays).
            </div>
          )
        )}
      </section>
    </>
  );
};
